const MAX_EVENTS = 800;

export type TraceKind =
  | "arrive"
  | "release"
  | "hold"
  | "seal"
  | "discard"
  | "start";

interface TraceEvent {
  at: number;
  kind: TraceKind;
  reason?: string;
  text?: string;
  startMs?: number;
  playedMs?: number;
  playing?: boolean;
  pending?: number;
  count?: number;
}

const events: TraceEvent[] = [];
let enabled = false;
let origin = 0;

const STORAGE_KEY = "chatTrailTrace";

const readFlag = (): boolean => {
  try {
    if (localStorage.getItem(STORAGE_KEY) === "0") return false;
    if (localStorage.getItem(STORAGE_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  // On by default while developing: the thing it diagnoses is intermittent,
  // and having to arm it first means the first reproduction is always lost.
  return import.meta.env.DEV;
};

export const traceEnabled = (): boolean => enabled;

/**
 * Whether to put the console hook on `window` at all.
 *
 * Always while developing; in production only once someone has armed it, so a
 * build carries nothing on the global until it is asked for.
 */
export const chatTrailTraceArmed = (): boolean => {
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const trace = (
  kind: TraceKind,
  data: Omit<TraceEvent, "at" | "kind">,
) => {
  if (!enabled) return;
  if (origin === 0) origin = Date.now();
  events.push({ at: Date.now() - origin, kind, ...data });
  if (events.length > MAX_EVENTS) events.shift();
};

const dump = (): string => {
  if (events.length === 0) {
    return enabled
      ? "chat trail trace is ON but recorded nothing — no tutor response yet?"
      : "chat trail trace is OFF — run __chatTrail.enable(), then reproduce.";
  }

  const head =
    "  t(ms)  kind      reason        start   played  playing  pending  text";
  const rows = events.map((e) =>
    [
      String(e.at).padStart(7),
      (e.kind ?? "").padEnd(9),
      (e.reason ?? "").padEnd(13),
      (e.startMs ?? "").toString().padStart(6),
      (e.playedMs === undefined ? "" : Math.round(e.playedMs))
        .toString()
        .padStart(8),
      (e.playing === undefined ? "" : e.playing ? "yes" : "NO").padStart(8),
      (e.pending ?? "").toString().padStart(8),
      "  " + (e.text ?? (e.count !== undefined ? `x${e.count}` : "")),
    ].join(" "),
  );
  return [head, ...rows].join("\n");
};

export const installChatTrailTrace = () => {
  enabled = readFlag();
  const api = {
    enable() {
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      enabled = true;
      events.length = 0;
      origin = 0;
      return "chat trail trace ON — reproduce, then copy(__chatTrail.dump())";
    },
    disable() {
      try {
        localStorage.setItem(STORAGE_KEY, "0");
      } catch {
        /* ignore */
      }
      enabled = false;
      return "chat trail trace OFF";
    },
    clear() {
      events.length = 0;
      origin = 0;
    },
    events,
    dump,
  };
  (window as unknown as { __chatTrail: typeof api }).__chatTrail = api;
};
